// src/pages/personal/analytics/MarketingExpensesSection/components/ExpenseFormModal.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody } from '@/components/ui/Modal';
import { Flex, Grid } from '@/components/ui/Layout';
import { 
  MarketingExpense, 
  useMarketingExpenses 
} from '../hooks/useMarketingExpenses';
import { formatDateForInput } from '@/utils/dateUtils';
import { useResponsive } from '@/hooks/useResponsive';

// Интерфейс для расширенного типа маркетинговых расходов
interface ExtendedMarketingExpense extends MarketingExpense {
  source_type?: string;
  external_id?: string;
  unique_reach?: number;
  ctr?: number;
  raw_data?: any;
}

// Опции для каналов
const LEAD_SOURCE_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'seo', label: 'SEO' },
  { value: 'maps', label: 'Карты' },
  { value: 'walk_in', label: 'Визит в магазин' },
  { value: 'referral', label: 'Реферал' },
  { value: 'other', label: 'Другое' }
];

// Платформы для расходов
const PLATFORM_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google Ads' },
  { value: '2gis', label: '2ГИС' },
  { value: 'other', label: 'Другие площадки' }
];

interface ExpenseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  expense: MarketingExpense | null;
}

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  expense
}) => {
  const { t } = useTranslation(['expenses', 'common']);
  useResponsive();
  const { isLoading, error, createExpense, updateExpense } = useMarketingExpenses();
  
  // Состояние для формы с расширенным типом
  const [formData, setFormData] = useState<Partial<ExtendedMarketingExpense>>({
    channel: '',
    platform: 'other',
    campaign_name: '',
    amount: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    impressions: 0,
    clicks: 0,
    source_type: 'manual',
    unique_reach: 0,
    ctr: 0
  });
  
  // Обновление формы при изменении расхода
  useEffect(() => {
    if (expense) {
      setFormData({
        channel: expense.channel,
        platform: expense.platform,
        campaign_name: expense.campaign_name,
        amount: expense.amount,
        start_date: formatDateForInput(expense.start_date),
        end_date: formatDateForInput(expense.end_date),
        impressions: expense.impressions || 0,
        clicks: expense.clicks || 0,
        source_type: (expense as ExtendedMarketingExpense).source_type || 'manual',
        unique_reach: (expense as ExtendedMarketingExpense).unique_reach || 0,
        ctr: (expense as ExtendedMarketingExpense).ctr || 0
      });
    } else {
      // Сброс формы для нового расхода
      setFormData({
        channel: '',
        platform: 'other',
        campaign_name: '',
        amount: 0,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        impressions: 0,
        clicks: 0,
        source_type: 'manual',
        unique_reach: 0,
        ctr: 0
      });
    }
  }, [expense]);
  
  // Обработчик изменения полей формы
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement | HTMLSelectElement;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };
  
  // Обработчик отправки формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let success = false;
    
    if (expense) {
      // Обновление существующего расхода
      success = await updateExpense(expense.id!, formData);
    } else {
      // Создание нового расхода
      success = await createExpense(formData as Omit<MarketingExpense, 'id' | 'business'>);
    }
    
    if (success && onSuccess) {
      onSuccess();
      onClose();
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      mobileMode="fullscreen"
      allowBackdropClose={true}
    >
      <ModalHeader onClose={onClose}>
        {expense 
          ? t('marketing.expenses.edit_title') 
          : t('marketing.expenses.add_title')}
      </ModalHeader>
      
      <ModalBody scrollable={true}>
        {error && (
          <div className="bg-red-100 text-red-800 p-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid 
            cols={2} 
            mobileCols={1}
            gap="md"
          >
            {/* Канал привлечения */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.channel')}*
              </label>
              <select
                name="channel"
                value={formData.channel || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">{t('common:select')}</option>
                {LEAD_SOURCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(`marketing.sources.${option.value}`, { defaultValue: option.label })}
                  </option>
                ))}
              </select>
            </div>

            {/* Платформа */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.platform')}*
              </label>
              <select
                name="platform"
                value={formData.platform || 'other'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                {PLATFORM_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(`marketing.platforms.${option.value}`, { defaultValue: option.label })}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Название кампании */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.campaign_name')}
              </label>
              <input
                type="text"
                name="campaign_name"
                value={formData.campaign_name || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            
            {/* Сумма расходов */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.amount')}*
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount || 0}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            {/* Дата начала */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.start_date')}*
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            
            {/* Дата окончания */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.end_date')}*
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Показы */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.impressions')}
              </label>
              <input
                type="number"
                name="impressions"
                value={formData.impressions || 0}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>

            {/* Клики */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.clicks')}
              </label>
              <input
                type="number"
                name="clicks"
                value={formData.clicks || 0}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>
            
            {/* Уникальный охват */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.unique_reach')}
              </label>
              <input
                type="number"
                name="unique_reach"
                value={formData.unique_reach || 0}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>
            
            {/* CTR (если уже рассчитан) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing.expenses.ctr')} (%)
              </label>
              <input
                type="number"
                name="ctr"
                value={formData.ctr || 0}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                max="100"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('marketing.expenses.ctr_help')}
              </p>
            </div>
          </Grid>

          <Flex 
            justify="end" 
            gap="sm" 
            className="mt-6"
            mobileDirection="column"
          >
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 w-full md:w-auto"
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 w-full md:w-auto"
              disabled={isLoading}
            >
              {isLoading ? t('common:saving') : (expense ? t('common:update') : t('common:save'))}
            </button>
          </Flex>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ExpenseFormModal;